import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Avatar,
  Checkbox,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Autocomplete,
  TextField,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  FindInPage as FindIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import axios from 'axios';
import { decrypt } from '../../../Security';
import { getRepresentativeImageUrl, handleImageError } from '../../../utils/imageUtils';

// ─── Step constants ───────────────────────────────────────────────────────────
const STEP_SELECT_STATE = 'SELECT_STATE';
const STEP_RESULTS      = 'RESULTS';

const DuplicateRepresentatives = () => {
  // Dialog & step control
  const [open, setOpen]     = useState(false);
  const [step, setStep]     = useState(STEP_SELECT_STATE);

  // State selection
  const [states, setStates]               = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [stateInput, setStateInput]       = useState('');

  // Results
  const [duplicateGroups, setDuplicateGroups]   = useState([]);
  const [totalRepsInState, setTotalRepsInState] = useState(0);
  const [loading, setLoading]                   = useState(false);
  const [loadingProgress, setLoadingProgress]   = useState(0);
  const [loadingMessage, setLoadingMessage]     = useState('');
  const [error, setError]                       = useState(null);

  // Delete
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [deleting, setDeleting]                   = useState(false);
  const [successMessage, setSuccessMessage]       = useState('');

  // ── Fetch distinct states ──────────────────────────────────────────────────
  const fetchStates = async () => {
    setStatesLoading(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/reps/states',
        { withCredentials: true }
      );
      const data = decrypt(response.data);
      setStates(data.states || []);
    } catch (err) {
      console.error('Error fetching states:', err);
      setError('Failed to load states. Please try again.');
    } finally {
      setStatesLoading(false);
    }
  };

  // ── Open dialog → load states ─────────────────────────────────────────────
  const handleOpenDialog = () => {
    setOpen(true);
    setStep(STEP_SELECT_STATE);
    setSelectedState(null);
    setStateInput('');
    setDuplicateGroups([]);
    setSelectedForDelete(new Set());
    setError(null);
    setSuccessMessage('');
    setTotalRepsInState(0);
    fetchStates();
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  // ── Find duplicates for the chosen state ──────────────────────────────────
  const handleFindDuplicates = async () => {
    if (!selectedState) return;

    setStep(STEP_RESULTS);
    setLoading(true);
    setLoadingProgress(10);
    setLoadingMessage(`Fetching representatives in ${selectedState}...`);
    setDuplicateGroups([]);
    setSelectedForDelete(new Set());
    setError(null);
    setSuccessMessage('');

    try {
      setLoadingProgress(40);
      const response = await axios.get(
        process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/reps/duplicates',
        {
          params: { state: selectedState },
          withCredentials: true,
          onDownloadProgress: (e) => {
            if (e.total) {
              const pct = Math.round((e.loaded * 30) / e.total);
              setLoadingProgress(40 + pct);
            }
          }
        }
      );

      setLoadingProgress(80);
      setLoadingMessage('Analyzing for name duplicates...');

      const data = decrypt(response.data);
      setTotalRepsInState(data.totalRepsInState || 0);

      if (!data.duplicateGroups || data.duplicateGroups.length === 0) {
        setError(`No duplicate names found among ${(data.totalRepsInState || 0).toLocaleString()} representatives in "${selectedState}".`);
      } else {
        setDuplicateGroups(data.duplicateGroups);
      }

      setLoadingProgress(100);
      setLoadingMessage('Done!');
    } catch (err) {
      console.error('Error finding duplicates:', err);
      setError('Failed to find duplicates. Please try again.');
    } finally {
      setTimeout(() => {
        setLoading(false);
        setLoadingProgress(0);
        setLoadingMessage('');
      }, 400);
    }
  };

  // ── Delete helpers ─────────────────────────────────────────────────────────
  const toggleSelectForDelete = (repId) => {
    const next = new Set(selectedForDelete);
    next.has(repId) ? next.delete(repId) : next.add(repId);
    setSelectedForDelete(next);
  };

  const selectAllInGroup = (idx) => {
    const next = new Set(selectedForDelete);
    duplicateGroups[idx].forEach((r) => next.add(r._id));
    setSelectedForDelete(next);
  };

  const deselectAllInGroup = (idx) => {
    const next = new Set(selectedForDelete);
    duplicateGroups[idx].forEach((r) => next.delete(r._id));
    setSelectedForDelete(next);
  };

  const selectDuplicatesOnly = () => {
    const next = new Set();
    duplicateGroups.forEach((group) => {
      for (let i = 1; i < group.length; i++) next.add(group[i]._id);
    });
    setSelectedForDelete(next);
  };

  const deleteSelectedRepresentatives = async () => {
    if (selectedForDelete.size === 0) {
      alert('Please select at least one representative to delete.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedForDelete.size} representative(s)? This cannot be undone.`)) return;

    setDeleting(true);
    let deletedCount = 0;
    let failedCount = 0;

    try {
      for (const repId of selectedForDelete) {
        try {
          const res = await fetch(
            process.env.REACT_APP_PUBLIC_SERVER_URI + '/adminWeb/deleteRep/' + repId,
            { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' }
          );
          const d = decrypt(await res.json());
          d.msg === 'SUCCESS' ? deletedCount++ : failedCount++;
        } catch {
          failedCount++;
        }
      }
      setSuccessMessage(
        `Successfully deleted ${deletedCount} representative(s)${failedCount > 0 ? `. Failed: ${failedCount}` : ''}.`
      );
      setTimeout(() => setSuccessMessage(''), 4000);
      // Re-run search for the same state
      await handleFindDuplicates();
    } catch (err) {
      console.error('Error deleting:', err);
      setError('An error occurred while deleting representatives.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Sub-component: one duplicate group ────────────────────────────────────
  const DuplicateGroup = ({ group, groupIndex }) => {
    // Helper to get full name from rep object
    const getFullName = (rep) => {
      if (rep.first_name || rep.last_name) {
        return `${rep.first_name || ''} ${rep.last_name || ''}`.trim();
      }
      return rep.name || 'N/A';
    };
    
    const groupName  = getFullName(group[0]);
    const groupState = group[0].state || 'N/A';
    
    return (
      <Paper elevation={2} sx={{ mb: 3, p: 2, backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#e65100', mb: 0.5 }}>
              Duplicate Group {groupIndex + 1} — <strong>{groupName}</strong> ({group.length} records)
            </Typography>
            <Chip label={`State: ${groupState}`} size="small" variant="outlined" color="warning" />
          </Box>
          <Box>
            <Button size="small" onClick={() => selectAllInGroup(groupIndex)} variant="outlined" sx={{ mr: 1 }}>
              Select All
            </Button>
            <Button size="small" onClick={() => deselectAllInGroup(groupIndex)} variant="outlined">
              Deselect All
            </Button>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#ffe0b2' }}>
                <TableCell align="center" sx={{ width: 50 }}>Select</TableCell>
                <TableCell align="center" sx={{ width: 70 }}>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Admin Area</TableCell>
                <TableCell>State</TableCell>
                <TableCell align="center" sx={{ width: 70 }}>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.map((rep, idx) => (
                <TableRow
                  key={rep._id}
                  sx={{
                    backgroundColor: selectedForDelete.has(rep._id)
                      ? '#ffccbc'
                      : idx % 2 === 0 ? '#fff9f7' : 'white',
                    '&:hover': { backgroundColor: selectedForDelete.has(rep._id) ? '#ffccbc' : '#ffeaa7' }
                  }}
                >
                  <TableCell align="center">
                    <Checkbox
                      checked={selectedForDelete.has(rep._id)}
                      onChange={() => toggleSelectForDelete(rep._id)}
                      color="warning"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Avatar
                      src={getRepresentativeImageUrl(rep.imagePath, 'small')}
                      alt={getFullName(rep)}
                      sx={{ width: 40, height: 40, mx: 'auto' }}
                      onError={(e) => handleImageError(e, 'small')}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{getFullName(rep)}</Typography>
                    <Typography variant="caption" color="textSecondary">ID: {rep._id}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{rep.official_email_id || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{rep.current_elected_position || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{rep.administrative_area || '—'}</Typography></TableCell>
                  <TableCell><Chip label={rep.state || '—'} size="small" variant="outlined" /></TableCell>
                  <TableCell align="center">
                    <Tooltip title={selectedForDelete.has(rep._id) ? 'Remove from selection' : 'Select for deletion'}>
                      <IconButton
                        size="small"
                        onClick={() => toggleSelectForDelete(rep._id)}
                        color={selectedForDelete.has(rep._id) ? 'error' : 'default'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Trigger button */}
      <Button
        variant="contained"
        color="warning"
        startIcon={<FindIcon />}
        onClick={handleOpenDialog}
        sx={{
          backgroundColor: '#ff9800',
          '&:hover': { backgroundColor: '#f57c00' },
          textTransform: 'none',
          fontWeight: 'bold'
        }}
      >
        Find Duplicates
      </Button>

      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: step === STEP_SELECT_STATE ? 'auto' : '80vh' } }}
      >
        {/* ── Dialog Title ── */}
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff3e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FindIcon sx={{ color: '#ff9800' }} />
            <Box>
              <Typography variant="h6">Find Duplicate Representatives</Typography>
              <Typography variant="caption" color="textSecondary">
                {step === STEP_SELECT_STATE
                  ? 'Select a state to search for name-based duplicates within it'
                  : `Showing name duplicates in "${selectedState}" — ${totalRepsInState.toLocaleString()} total reps`}
              </Typography>
            </Box>
            {step === STEP_RESULTS && duplicateGroups.length > 0 && (
              <Chip label={`${duplicateGroups.length} duplicate group(s)`} color="error" />
            )}
          </Box>
          <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        {/* ── Dialog Content ── */}
        <DialogContent sx={{ pt: 3 }}>

          {/* ── STEP 1: State selector ── */}
          {step === STEP_SELECT_STATE && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
              <LocationIcon sx={{ fontSize: 64, color: '#ff9800' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
                Select a State
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', maxWidth: 480 }}>
                Choose a state to search for representatives who share the same name. Both records will be displayed so you can review and remove the duplicate.
              </Typography>

              <Autocomplete
                options={states}
                value={selectedState}
                inputValue={stateInput}
                onChange={(_, v) => setSelectedState(v)}
                onInputChange={(_, v) => setStateInput(v)}
                loading={statesLoading}
                sx={{ width: '100%', maxWidth: 420 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search & select a state"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#ff9800' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {statesLoading && <CircularProgress color="inherit" size={18} />}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />

              {error && (
                <Alert severity="error" sx={{ width: '100%', maxWidth: 420 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {/* ── STEP 2: Results ── */}
          {step === STEP_RESULTS && (
            <>
              {/* Loading */}
              {loading && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 300, gap: 3, justifyContent: 'center' }}>
                  <CircularProgress size={60} thickness={4} sx={{ color: '#ff9800' }} />
                  <Box sx={{ width: '80%', maxWidth: 500 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{loadingMessage}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#ff9800' }}>{loadingProgress}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={loadingProgress}
                      sx={{
                        height: 10, borderRadius: 5, backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': { borderRadius: 5, backgroundColor: loadingProgress === 100 ? '#4caf50' : '#ff9800' }
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Searching representatives in "{selectedState}"...
                  </Typography>
                </Box>
              )}

              {/* Success */}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
                  {successMessage}
                </Alert>
              )}

              {/* Error / no results */}
              {error && !loading && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Results list */}
              {!loading && duplicateGroups.length > 0 && (
                <Box>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#fafafa' }}>
                    <Typography variant="body1">
                      <strong>
                        Found {duplicateGroups.length} duplicate name group(s) with{' '}
                        {duplicateGroups.reduce((t, g) => t + g.length, 0)} records
                      </strong>{' '}
                      out of {totalRepsInState.toLocaleString()} representatives in <em>{selectedState}</em>.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      Select the records you want to delete, then click "Delete Selected".
                    </Typography>
                  </Paper>

                  {duplicateGroups.map((group, idx) => (
                    <DuplicateGroup key={idx} group={group} groupIndex={idx} />
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* ── Dialog Actions ── */}
        <DialogActions sx={{ backgroundColor: '#fafafa', p: 2, gap: 1, flexWrap: 'wrap' }}>
          {/* Selection count */}
          {step === STEP_RESULTS && duplicateGroups.length > 0 && (
            <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
              {selectedForDelete.size} representative(s) selected for deletion
            </Typography>
          )}

          {/* Back to state selector */}
          {step === STEP_RESULTS && !loading && (
            <Button
              onClick={() => {
                setStep(STEP_SELECT_STATE);
                setDuplicateGroups([]);
                setError(null);
                setSuccessMessage('');
                setSelectedForDelete(new Set());
              }}
              variant="outlined"
              color="inherit"
            >
              ← Change State
            </Button>
          )}

          <Button onClick={handleCloseDialog} color="primary">Close</Button>

          {/* Step 1 → Find button */}
          {step === STEP_SELECT_STATE && (
            <Button
              onClick={handleFindDuplicates}
              variant="contained"
              color="warning"
              disabled={!selectedState || statesLoading}
              startIcon={<SearchIcon />}
              sx={{ backgroundColor: '#ff9800', '&:hover': { backgroundColor: '#f57c00' } }}
            >
              Find Duplicates in {selectedState || '…'}
            </Button>
          )}

          {/* Step 2 → delete actions */}
          {step === STEP_RESULTS && duplicateGroups.length > 0 && !loading && (
            <>
              <Button onClick={selectDuplicatesOnly} color="warning" variant="outlined">
                Auto-select Duplicates
              </Button>
              <Button
                onClick={deleteSelectedRepresentatives}
                color="error"
                variant="contained"
                disabled={selectedForDelete.size === 0 || deleting}
                startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
              >
                {deleting ? 'Deleting…' : `Delete Selected (${selectedForDelete.size})`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DuplicateRepresentatives;
